import { Card } from "@/components/ui/card";
import { format } from "date-fns";
import { Download, Mic, FileText, Sparkles, ZoomIn, Loader2, RefreshCw, Video, User } from "lucide-react";
import { Dialog, DialogContent, DialogTrigger, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import PDFViewer from "./PDFViewer";

interface Attachment {
  type: "image" | "audio" | "document" | "video" | "contact";
  url: string;
  fileName?: string;
  aiDescription?: string;
  transcription?: string;
  contactData?: {
    name?: string;
    phone?: string[];
    email?: string[];
    organization?: string;
  };
}

interface Message {
  id: string;
  sender: string;
  content: string;
  timestamp: Date;
  isSent: boolean;
  isSystemMessage?: boolean;
  attachment?: Attachment;
}

interface ChatMessageProps {
  message: Message;
  isProcessing?: boolean;
  onRerunAI?: (messageId: string) => void;
  disableRerun?: boolean;
}

const ChatMessage = ({ message, isProcessing = false, onRerunAI, disableRerun = false }: ChatMessageProps) => {
  const { t } = useLanguage();
  const { sender, content, timestamp, isSent, attachment, isSystemMessage } = message;
  const [audioDuration, setAudioDuration] = useState<string>("0:00");
  
  // Remove <attached: ...> text if attachment exists
  const displayContent = attachment 
    ? content.replace(/<attached:[^>]+>/gi, '').trim()
    : content;

  // Render system message differently
  if (isSystemMessage) {
    return (
      <div className="flex justify-center my-4">
        <div className="px-4 py-2 rounded-full bg-muted/50 text-muted-foreground text-sm text-center max-w-[80%]">
          {displayContent}
        </div>
      </div>
    );
  }

  // Function to linkify text
  const linkifyText = (text: string) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    
    return parts.map((part, index) => {
      if (part.match(urlRegex)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline hover:opacity-80 transition-opacity"
            onClick={(e) => e.stopPropagation()}
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  const handleDownload = async (url: string, filename: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const handleAudioLoadedMetadata = (e: React.SyntheticEvent<HTMLAudioElement>) => {
    const audio = e.currentTarget;
    const minutes = Math.floor(audio.duration / 60);
    const seconds = Math.floor(audio.duration % 60);
    setAudioDuration(`${minutes}:${seconds.toString().padStart(2, '0')}`);
  };

  const getDocumentPreviewUrl = (url: string, fileName?: string) => {
    if (!fileName) return { url, isPdf: true };
    
    const extension = fileName.toLowerCase().split('.').pop();
    const isPdf = extension === 'pdf';
    
    // For Office documents, use Microsoft Office Online Viewer (works better with signed URLs)
    if (!isPdf && ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(extension || '')) {
      return { 
        url: `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(url)}`,
        isPdf: false
      };
    }
    
    return { url, isPdf };
  };

  return (
    <div className={`flex ${isSent ? "justify-end" : "justify-start"} animate-in fade-in slide-in-from-bottom-2`}>
      <div className={`max-w-[85%] sm:max-w-[80%] md:max-w-[75%] ${isSent ? "items-end" : "items-start"} flex flex-col gap-1`}>
        {!isSent && (
          <span className="text-xs font-medium text-muted-foreground px-2 sm:px-3">{sender}</span>
        )}
        
        <Card
          className={`p-3 sm:p-4 shadow-chat transition-smooth hover:shadow-soft ${
            isSent
              ? "bg-chat-sent text-chat-sent-foreground"
              : "bg-chat-received text-chat-received-foreground"
          }`}
        >
          {displayContent && (
            <p className="text-xs sm:text-sm leading-relaxed whitespace-pre-wrap">
              {linkifyText(displayContent)}
            </p>
          )}

          {attachment && (
            <div className="mt-3 space-y-2">
              {attachment.type === "image" && (
                <div className="rounded-lg overflow-hidden">
                  <Dialog>
                    <DialogTrigger asChild>
                      <div className="relative group cursor-pointer">
                        <img
                          src={attachment.url}
                          alt="Attachment"
                          className="w-full max-w-[280px] sm:max-w-sm rounded-lg transition-opacity group-hover:opacity-90"
                        />
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/20 rounded-lg">
                          <ZoomIn className="w-8 h-8 text-white" />
                        </div>
                      </div>
                    </DialogTrigger>
                    <DialogContent className="max-w-[90vw] max-h-[90vh] p-0">
                      <DialogTitle className="sr-only">
                        {t("message.imageAttachment") || "Image attachment"}
                      </DialogTitle>
                      <DialogDescription className="sr-only">
                        {t("message.imagePreview") || "Image preview"}
                      </DialogDescription>
                      <img
                        src={attachment.url}
                        alt="Attachment"
                        className="w-full h-full object-contain"
                      />
                    </DialogContent>
                  </Dialog>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDownload(attachment.url, `image-${message.id}.jpg`)}
                    className="mt-2 w-full"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {t("message.download")}
                  </Button>
                  {isProcessing && (
                    <div className="mt-2 p-3 bg-background/50 rounded-lg border">
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 text-primary animate-spin flex-shrink-0" />
                        <p className="text-xs text-muted-foreground">
                          {t("message.aiAnalyzing")}
                        </p>
                      </div>
                    </div>
                  )}
                  {!isProcessing && attachment.aiDescription && (
                    <div className="mt-2 p-3 bg-background/50 rounded-lg border">
                      <div className="flex items-start gap-2">
                        <Sparkles className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-xs text-muted-foreground">
                            <span className="font-semibold">{t("message.aiAnalysis")}</span> {attachment.aiDescription}
                          </p>
                        </div>
                        {onRerunAI && !disableRerun && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onRerunAI(message.id)}
                            className="h-auto p-1 hover:bg-background/80"
                            title={t("message.rerunAnalysis")}
                          >
                            <RefreshCw className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {attachment.type === "audio" && (
                <div className="p-3 bg-background/50 rounded-lg border space-y-2">
                  <div className="flex items-center gap-3">
                    <Mic className="w-5 h-5 text-accent flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <audio 
                        controls 
                        className="w-full h-8"
                        onLoadedMetadata={handleAudioLoadedMetadata}
                      >
                        <source src={attachment.url} />
                      </audio>
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{audioDuration}</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDownload(attachment.url, `audio-${message.id}.opus`)}
                    className="w-full"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {t("message.download")}
                  </Button>
                  {isProcessing && (
                    <div className="flex items-center gap-2 pt-2">
                      <Loader2 className="w-4 h-4 text-primary animate-spin flex-shrink-0" />
                      <p className="text-xs text-muted-foreground">
                        {t("message.aiTranscribing")}
                      </p>
                    </div>
                  )}
                  {!isProcessing && attachment.transcription && (
                    <div className="flex items-start gap-2 pt-2">
                      <Sparkles className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-xs text-muted-foreground">
                          <span className="font-semibold">{t("message.transcription")}</span> {attachment.transcription}
                        </p>
                      </div>
                      {onRerunAI && !disableRerun && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onRerunAI(message.id)}
                          className="h-auto p-1 hover:bg-background/80"
                          title={t("message.rerunTranscription")}
                        >
                          <RefreshCw className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {attachment.type === "document" && (
                <div className="p-3 bg-background/50 rounded-lg border space-y-2">
                  <Dialog>
                    <DialogTrigger asChild>
                      <div 
                        className="flex items-center gap-3 cursor-pointer hover:bg-background/70 transition-colors rounded p-2 -m-2"
                      >
                        <FileText className="w-5 h-5 text-primary flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {attachment.fileName || t("viewer.documents")}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {t("message.clickToOpen")}
                          </p>
                        </div>
                      </div>
                    </DialogTrigger>
                    <DialogContent className="max-w-[90vw] max-h-[90vh] w-full h-full p-0">
                      <DialogTitle className="sr-only">
                        {attachment.fileName || t("viewer.documents")}
                      </DialogTitle>
                      <DialogDescription className="sr-only">
                        {t("message.documentPreview") || "Document preview"}
                      </DialogDescription>
                      {(() => {
                        const { url: previewUrl, isPdf } = getDocumentPreviewUrl(attachment.url, attachment.fileName);
                        
                        if (isPdf) {
                          return <PDFViewer fileUrl={attachment.url} />;
                        } else {
                          return (
                            <iframe
                              src={previewUrl}
                              className="w-full h-full rounded-lg border-0"
                              title={attachment.fileName || "Document"}
                            />
                          );
                        }
                      })()}
                    </DialogContent>
                  </Dialog>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDownload(attachment.url, attachment.fileName || `document-${message.id}.pdf`)}
                    className="w-full"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {t("message.download")}
                  </Button>
                  {isProcessing && (
                    <div className="flex items-center gap-2 pt-2">
                      <Loader2 className="w-4 h-4 text-primary animate-spin flex-shrink-0" />
                      <p className="text-xs text-muted-foreground">
                        {t("message.aiAnalyzingDoc")}
                      </p>
                    </div>
                  )}
                  {!isProcessing && attachment.aiDescription && (
                    <div className="flex items-start gap-2 pt-2">
                      <Sparkles className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-xs text-muted-foreground">
                          <span className="font-semibold">{t("message.content")}</span> {attachment.aiDescription}
                        </p>
                      </div>
                      {onRerunAI && !disableRerun && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onRerunAI(message.id)}
                          className="h-auto p-1 hover:bg-background/80"
                          title={t("message.rerunAnalysis")}
                        >
                          <RefreshCw className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {attachment.type === "video" && (
                <div className="p-3 bg-background/50 rounded-lg border space-y-2">
                  <video 
                    controls 
                    className="w-full max-w-[280px] sm:max-w-sm rounded-lg"
                    preload="metadata"
                  >
                    <source src={attachment.url} />
                  </video>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDownload(attachment.url, attachment.fileName || `video-${message.id}.mp4`)}
                    className="w-full"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {t("message.download")}
                  </Button>
                  {isProcessing && (
                    <div className="flex items-center gap-2 pt-2">
                      <Loader2 className="w-4 h-4 text-primary animate-spin flex-shrink-0" />
                      <p className="text-xs text-muted-foreground">
                        {t("message.aiAnalyzingVideo")}
                      </p>
                    </div>
                  )}
                  {!isProcessing && attachment.aiDescription && (
                    <div className="flex items-start gap-2 pt-2">
                      <Sparkles className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-xs text-muted-foreground">
                          <span className="font-semibold">{t("message.videoDescription")}</span> {attachment.aiDescription}
                        </p>
                      </div>
                      {onRerunAI && !disableRerun && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onRerunAI(message.id)}
                          className="h-auto p-1 hover:bg-background/80"
                          title={t("message.rerunAnalysis")}
                        >
                          <RefreshCw className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {attachment.type === "contact" && (
                <div className="p-3 bg-background/50 rounded-lg border space-y-2">
                  <Dialog>
                    <DialogTrigger asChild>
                      <div className="flex items-start gap-3 cursor-pointer hover:bg-background/70 transition-colors rounded p-2 -m-2">
                        <User className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">
                            {attachment.contactData?.name || t("message.contact")}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {t("message.clickToOpen")}
                          </p>
                        </div>
                      </div>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                      <DialogTitle className="sr-only">
                        {attachment.contactData?.name || t("message.contact")}
                      </DialogTitle>
                      <DialogDescription className="sr-only">
                        {t("message.contactDetails") || "Contact details"}
                      </DialogDescription>
                      <div className="space-y-4 pt-4">
                        <div className="flex items-center gap-3">
                          <User className="w-8 h-8 text-primary flex-shrink-0" />
                          <div>
                            <p className="text-lg font-semibold">
                              {attachment.contactData?.name || t("message.contact")}
                            </p>
                            {attachment.contactData?.organization && (
                              <p className="text-sm text-muted-foreground">
                                {attachment.contactData.organization}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        {attachment.contactData?.phone && attachment.contactData.phone.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-sm font-medium text-muted-foreground">טלפון</p>
                            {attachment.contactData.phone.map((phone, idx) => (
                              <p key={idx} className="text-sm">
                                📞 {phone}
                              </p>
                            ))}
                          </div>
                        )}
                        
                        {attachment.contactData?.email && attachment.contactData.email.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-sm font-medium text-muted-foreground">אימייל</p>
                            {attachment.contactData.email.map((email, idx) => (
                              <p key={idx} className="text-sm">
                                ✉️ {email}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    </DialogContent>
                  </Dialog>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDownload(attachment.url, attachment.fileName || `contact-${message.id}.vcf`)}
                    className="w-full"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {t("message.download")}
                  </Button>
                  {isProcessing && (
                    <div className="flex items-center gap-2 pt-2">
                      <Loader2 className="w-4 h-4 text-primary animate-spin flex-shrink-0" />
                      <p className="text-xs text-muted-foreground">
                        {t("message.aiAnalyzingContact")}
                      </p>
                    </div>
                  )}
                  {!isProcessing && attachment.aiDescription && (
                    <div className="flex items-start gap-2 pt-2">
                      <Sparkles className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-xs text-muted-foreground">
                          {attachment.aiDescription}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </Card>

        <span className="text-xs text-muted-foreground px-2 sm:px-3">
          {format(timestamp, "MMM d, h:mm a")}
        </span>
      </div>
    </div>
  );
};

export default ChatMessage;
